import React, { useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Field, FieldRenderProps } from 'react-final-form';
import { Typography } from '../Typography';
import { InfoTooltip } from '../Tooltip/InfoTooltip';
import Bottom from '../../assets/VectorBottom.svg';
import Top from '../../assets/VectorTop.svg';

interface Genre {
  id: number;
  name: string;
  category: string;
  parent_id: number | null;
}

interface FormMultiSelectGanerProps {
  name: string;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  genres: Genre[];
  initialValue?: number[];
  selectionLimit?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  placeholder?: string;
  Main?: boolean;
  required?: boolean;
}

export const FormMultiSelectGaner: React.FC<FormMultiSelectGanerProps> = ({
  name,
  label,
  tooltip,
  genres,
  initialValue = [],
  selectionLimit = 3,
  isOpen,
  setIsOpen,
  placeholder,
  Main = false,
  required = false,
}) => {
  return (
    <Field<number[]>
      name={name}
      initialValue={initialValue}
      validate={(value) =>
        required && (!value || value.length === 0) ? 'This field is required' : undefined
      }
      render={({ input, meta }: FieldRenderProps<number[]>) => (
        <>
          <InputMultiSelect
            input={input}
            genres={genres}
            label={label}
            tooltip={tooltip}
            selectionLimit={selectionLimit}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            placeholder={placeholder}
            Main={Main}
            required={required} 
          />
          {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
        </>
      )}
    />
  );
};

interface InputMultiSelectProps {
  input: FieldRenderProps<number[]>['input'];
  genres: Genre[];
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  selectionLimit?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  placeholder?: string;
  Main?: boolean;
  required?: boolean;

}

const InputMultiSelect: React.FC<InputMultiSelectProps> = ({
  input,
  genres,
  label,
  tooltip,
  selectionLimit = 3,
  isOpen,
  setIsOpen,
  placeholder,
  Main,
  required=false
}) => {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const categories = useMemo(() => {
    return Object.entries(
      genres.reduce((acc, genre) => {
        if (!acc[genre.category]) acc[genre.category] = [];
        acc[genre.category].push(genre);
        return acc;
      }, {} as Record<string, Genre[]>)
    ).map(([category, items]) => {
      const parents = items.filter((g) => g.parent_id === null);
      const childrenMap: Record<number, Genre[]> = {};
      parents.forEach((p) => {
        childrenMap[p.id] = items.filter((g) => g.parent_id === p.id);
      });
      return { category, parents, childrenMap };
    });
  }, [genres]);

  const toggleChild = (id: number) => {
    const current = input.value || [];
    if (current.includes(id)) {
      input.onChange(current.filter((v) => v !== id));
    } else {
      let next: number[] = [];
      if (current.length >= (selectionLimit || 3)) {
        next = [...current.slice(1), id];
      } else {
        next = [...current, id];
      }
      input.onChange(next);
    }
  };

  const selectedNames = useMemo(
    () => genres.filter((g) => input.value?.includes(g.id)).map((g) => g.name),
    [input.value, genres]
  );

  return (
    <Container ref={ref}>
      {label && (
        <Label>
          <Typography variant="body6">
            <label>

              {label} ({input.value?.length || 0}/{selectionLimit})
              {required && <span style={{ color: '#6777FF' }}>*</span>}
            </label>
          </Typography>
          {tooltip && <InfoTooltip name={`select-tooltip-${input.name}`} tooltip={tooltip} />}
        </Label>
      )}

      <Input onClick={() => setIsOpen(!isOpen)} style={{ border: isOpen ? '1px solid #000000' : '1px solid #E9E9E9' }}>
        {/* {!selectedNames.length && Main && (
          <PrimBox>
            <div>RAP</div>
            <div>Cloud rap</div>
          </PrimBox>
        )} */}
        <span>
          <span style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
            {!selectedNames.length && placeholder}
          </span>{' '}
          {selectedNames.join(', ')}
        </span>{' '}
        {isOpen ? <Top /> : <Bottom />}
      </Input>

      {isOpen && (
        <ParentsBox>
          <Box>
            {categories.map(({ category, parents, childrenMap }) => (
              <CategoryBlock key={category}>
                <CategoryTitle
                  onClick={() =>
                    setOpenCategory(openCategory === category ? null : category)
                  }
                  style={{ color: openCategory === category ? '#00000080' : '#000000' }}
                >
                  {category} {openCategory === category ? <Top /> : <Bottom />}
                </CategoryTitle>

                {openCategory === category &&
                  parents.map((parent) => (
                    <ParentBlock key={parent.id}>
                      <Parent>{parent.name}</Parent>
                      <Dropdown>
                        <Option
                          selected={input.value?.includes(parent.id) || false}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleChild(parent.id);
                          }}
                        >
                          {parent.name}
                        </Option>
                        {childrenMap[parent.id].map((child) => (
                          <Option
                            key={child.id}
                            selected={input.value?.includes(child.id) || false}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleChild(child.id);
                            }}
                          >
                            {child.name}
                          </Option>
                        ))}
                      </Dropdown>
                    </ParentBlock>
                  ))}
              </CategoryBlock>
            ))}
          </Box>
        </ParentsBox>
      )}
    </Container>
  );
};

// ======= Styled Components =======

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
`;

const Box = styled.div`
  width: calc(100% - 44px);
  display: flex;
  flex-direction: column;
  gap: 21px;
  position: absolute;
`;

const ParentBlock = styled.div`
  padding: 11px 0 0 8px;
`;

const Parent = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 13px;
  line-height: 18px;
  color: #000000;
  cursor: pointer;
`;

const Dropdown = styled.div`
  margin-top: 6px;
  border-radius: 8px;
  background: white;
`;

const Option = styled.div<{ selected: boolean }>`
  padding: 8px;
  cursor: pointer;
  background: ${({ selected }) => (selected ? '#e0f0ff' : 'white')};
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: #000000;
  &:hover {
    background: #f5f5f5;
  }
`;

const Label = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
  cursor: pointer;
`;

const Input = styled.div`
  width: 100%;
  height: 48px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5px;
  cursor: pointer;
`;

const ParentsBox = styled.div`
  width: 100%;
  height: 298px;
  overflow-y: auto;
  position: absolute;
  top: 80px;
  z-index: 20;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  background: #ffffff;
  padding: 26px 23px 9px 21px;
`;

const CategoryBlock = styled.div`
  width: 100%;
`;

const CategoryTitle = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
`;

const PrimBox = styled.div`
  width: 72px;
  height: 33px;
  border-radius: 6px;
  background-color: rgba(103, 119, 255, 0.5);
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 11px;
  line-height: 15px;
  color: rgba(255, 255, 255, 1);
  padding: 3px 0 0 7px;
`;

const ErrorText = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;
